import { createSlice } from "@reduxjs/toolkit";
import { apiCaller } from "../../api/apiCaller";
import { bonusApi, boostedOddsApis } from "../../sharedfiles/EndpointConfig";
import { toaster } from "../../sharedfiles/helper";

/**
 * Thin slice for the Create Bonus V2 wizard.
 * Reuses the same POST /bonus/create endpoint as the legacy page
 * (bonusApi.getBonus) but keeps its own submit/result state so the wizard
 * can show inline success/failure without touching the legacy slice.
 *
 * Also owns the cascading scope reference data (sport → country → league →
 * event → market) for the "Where can it be used?" panel. Same feed/* APIs as
 * the old page's selection modal, but with V2-local loading flags so the
 * dropdowns can show a loading placeholder.
 */
export const CreateBonusV2Slice = createSlice({
  name: "bonusV2",
  initialState: {
    submitting: false,
    // null | { success: boolean, message: string }
    result: null,
    // Cascading scope dropdown data — raw API shapes, mapped in the component.
    scopeCountries: [],
    scopeLeagues: [],
    scopeEvents: [],
    scopeMarkets: [],
    countriesLoading: false,
    leaguesLoading: false,
    eventsLoading: false,
    marketsLoading: false,
  },
  reducers: {
    setSubmitting: (state, action) => {
      state.submitting = action.payload;
    },
    setResult: (state, action) => {
      state.result = action.payload;
    },
    resetCreateState: (state) => {
      state.submitting = false;
      state.result = null;
    },
    setScopeCountries: (state, action) => {
      state.scopeCountries = action.payload;
    },
    setScopeLeagues: (state, action) => {
      state.scopeLeagues = action.payload;
    },
    setScopeEvents: (state, action) => {
      state.scopeEvents = action.payload;
    },
    setScopeMarkets: (state, action) => {
      state.scopeMarkets = action.payload;
    },
    setCountriesLoading: (state, action) => {
      state.countriesLoading = action.payload;
    },
    setLeaguesLoading: (state, action) => {
      state.leaguesLoading = action.payload;
    },
    setEventsLoading: (state, action) => {
      state.eventsLoading = action.payload;
    },
    setMarketsLoading: (state, action) => {
      state.marketsLoading = action.payload;
    },
  },
});

export const {
  setSubmitting,
  setResult,
  resetCreateState,
  setScopeCountries,
  setScopeLeagues,
  setScopeEvents,
  setScopeMarkets,
  setCountriesLoading,
  setLeaguesLoading,
  setEventsLoading,
  setMarketsLoading,
} = CreateBonusV2Slice.actions;

const humanize = (msg) =>
  typeof msg === "string" && msg
    ? msg
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ")
    : "Bonus creation failed";

// Resolves to the same { success, message } object it stores on the slice, so
// the edit flow can chain the deactivation of the original campaign onto a
// successful create.
export const createBonusV2 = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  dispatch(setResult(null));
  return apiCaller(bonusApi.getBonus, "POST", payload)
    .then((response) => {
      const ok =
        response &&
        response.status === 200 &&
        response.message === "CREATE_BONUS_SUCCESS";
      const result = ok
        ? { success: true, message: response.message, data: response.data }
        : { success: false, message: humanize(response && response.message) };
      if (ok) {
        toaster.success("Bonus created successfully");
      } else {
        toaster.error(result.message);
      }
      dispatch(setResult(result));
      return result;
    })
    .catch((error) => {
      const result = {
        success: false,
        message: humanize(error && error.response),
      };
      toaster.error(result.message);
      dispatch(setResult(result));
      return result;
    })
    .finally(() => {
      dispatch(setSubmitting(false));
    });
};

// ─── Bonus code availability ─────────────────────────────────────────────────
// Plain promise helpers, no slice state: MANUAL/DIRECT bonus codes are unique
// on the backend across every status (BonusService.createBonus looks the code
// up without filtering on status), so a replacement campaign can never reuse
// the code of the campaign it replaces.

/** True when a bonus with exactly this code already exists, in any status. */
export const isBonusCodeTaken = (code) => {
  const trimmed = String(code || "").trim();
  if (!trimmed) return Promise.resolve(false);
  const url = `${bonusApi.getBonusummery}?bonusCode=${encodeURIComponent(
    trimmed
  )}&pageNumber=0&itemsPerPage=25`;
  return apiCaller(url, "GET", {})
    .then((res) => {
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      // The list filter matches loosely — only an exact code counts as taken.
      return rows.some(
        (row) =>
          String(row.bonusCode || "").toLowerCase() === trimmed.toLowerCase()
      );
    })
    // Can't tell: let the operator through and let /bonus/create be the judge.
    .catch(() => false);
};

const MAX_CODE_LENGTH = 20;

/** "ABC123-V2" → "ABC123" so a re-edit doesn't build ABC123-V2-V3. */
const codeStem = (code) => String(code || "").trim().replace(/-V\d+$/i, "");

/**
 * First free "<code>-V<n>" variant (V2, V3, …) of an existing bonus code.
 * Returns "" when there is no code to vary (AUTO campaigns have none) and the
 * last candidate tried when every variant up to V20 is taken.
 */
export const suggestReplacementCode = async (originalCode) => {
  const stem = codeStem(originalCode);
  if (!stem) return "";
  let candidate = "";
  for (let n = 2; n <= 20; n += 1) {
    const suffix = `-V${n}`;
    candidate = `${stem.slice(0, MAX_CODE_LENGTH - suffix.length)}${suffix}`;
    // Sequential on purpose: stop at the first code nobody else owns.
    // eslint-disable-next-line no-await-in-loop
    const taken = await isBonusCodeTaken(candidate);
    if (!taken) return candidate;
  }
  return candidate;
};

// ─── Free spin config (edit/duplicate prefill) ───────────────────────────────

/**
 * Best-effort fetch of a bonus's free spin rows for the edit/duplicate
 * prefill: GET {PBS}/free-spin/{bonusId}/config (same endpoint the classic
 * table's "view free spins" dialog uses).
 *
 * Resolves { rows, sharedMinDeposit, sharedMaxWin } in the wizard's row shape,
 * or null when the endpoint fails / returns nothing — callers show a notice
 * and let the operator re-add the rows by hand.
 */
export const fetchFreeSpinRows = (bonusId) =>
  apiCaller(bonusApi.getFreeSpinConfig(bonusId), "GET", {})
    .then((res) => {
      const data = Array.isArray(res?.data) ? res.data : [];
      if (res?.status !== 200 || data.length === 0) return null;
      const rows = data.map((item) => ({
        providerName: item.providerName || "",
        subProviderName: item.subProviderName || "",
        gameName: item.gameName || "",
        gameId: item.gameId != null ? item.gameId : null,
        gameCode: item.gameCode || null,
        freeSpinAmount:
          item.freeSpinAmount != null ? String(item.freeSpinAmount) : "",
        numberOfFreeSpins:
          item.numberOfFreeSpins != null ? String(item.numberOfFreeSpins) : "",
      }));
      const first = data[0] || {};
      return {
        rows,
        // The backend stamps the shared body fields onto every row — read
        // them back from the first row when the bonus itself lacks them.
        sharedMinDeposit:
          first.freeSpinMinDepositAmount != null && first.freeSpinMinDepositAmount !== 0
            ? String(first.freeSpinMinDepositAmount)
            : "",
        sharedMaxWin:
          first.freeSpinMaxWinAmount != null && first.freeSpinMaxWinAmount !== 0
            ? String(first.freeSpinMaxWinAmount)
            : "",
      };
    })
    .catch(() => null);

// ─── Cascading scope thunks ──────────────────────────────────────────────────
// Response shapes mirror what the old page's modal (BonusUsageDDLs.js /
// boostedOddsSlice.js) already consumes:
//   countries → res.data.countries: [{ countryId, countryName }]
//   leagues   → res.data.leagues:   [{ leagueId, leagueName }]
//   events    → res.data.events:    [{ eventId, eventName, eventStartDate }]
//   markets   → res.data.markets:   [{ marketId, marketName }]

export const fetchScopeCountries = (sportId) => (dispatch) => {
  dispatch(setCountriesLoading(true));
  dispatch(setScopeCountries([]));
  return apiCaller(boostedOddsApis.getAllCountriesBySport(sportId), "GET", {})
    .then((res) => {
      if (res.status === 200 && Array.isArray(res.data?.countries)) {
        dispatch(setScopeCountries(res.data.countries));
      }
    })
    .catch(() => {
      dispatch(setScopeCountries([]));
    })
    .finally(() => {
      dispatch(setCountriesLoading(false));
    });
};

export const fetchScopeLeagues = (sportId, countryId) => (dispatch) => {
  dispatch(setLeaguesLoading(true));
  dispatch(setScopeLeagues([]));
  return apiCaller(
    boostedOddsApis.getLeaguesBysportAndCountry(sportId, countryId),
    "GET",
    {}
  )
    .then((res) => {
      if (res.status === 200 && Array.isArray(res.data?.leagues)) {
        dispatch(setScopeLeagues(res.data.leagues));
      }
    })
    .catch(() => {
      dispatch(setScopeLeagues([]));
    })
    .finally(() => {
      dispatch(setLeaguesLoading(false));
    });
};

export const fetchScopeEvents = (sportId, countryId, leagueId) => (dispatch) => {
  dispatch(setEventsLoading(true));
  dispatch(setScopeEvents([]));
  return apiCaller(
    boostedOddsApis.getEventsBySportCountryLeague(sportId, countryId, leagueId),
    "GET",
    {}
  )
    .then((res) => {
      if (res.status === 200 && Array.isArray(res.data?.events)) {
        dispatch(setScopeEvents(res.data.events));
      }
    })
    .catch(() => {
      dispatch(setScopeEvents([]));
    })
    .finally(() => {
      dispatch(setEventsLoading(false));
    });
};

export const fetchScopeMarkets = (eventId) => (dispatch) => {
  dispatch(setMarketsLoading(true));
  return apiCaller(boostedOddsApis.getAllMarketsByEvent(eventId), "GET", {})
    .then((res) => {
      if (res.status === 200 && Array.isArray(res.data?.markets)) {
        dispatch(setScopeMarkets(res.data.markets));
      }
    })
    .catch(() => {
      dispatch(setScopeMarkets([]));
    })
    .finally(() => {
      dispatch(setMarketsLoading(false));
    });
};

// The feed only returns markets for events that are already booked/traded — a
// far-future or untraded event legitimately answers with zero fixtures. Market
// template ids are constant across events, so any one traded event yields the
// whole catalogue: try candidates in turn and keep the first non-empty answer.
export const fetchScopeMarketsFromCandidates =
  (eventIds = []) =>
  async (dispatch) => {
    const candidates = eventIds.filter(Boolean).slice(0, 5);
    if (!candidates.length) return;
    dispatch(setMarketsLoading(true));
    try {
      for (let i = 0; i < candidates.length; i += 1) {
        // sequential on purpose: stop at the first event that has markets
        // eslint-disable-next-line no-await-in-loop
        const res = await apiCaller(
          boostedOddsApis.getAllMarketsByEvent(candidates[i]),
          "GET",
          {}
        ).catch(() => null);
        const markets = res?.data?.markets;
        if (res?.status === 200 && Array.isArray(markets) && markets.length) {
          dispatch(setScopeMarkets(markets));
          return;
        }
      }
      dispatch(setScopeMarkets([]));
    } finally {
      dispatch(setMarketsLoading(false));
    }
  };

export default CreateBonusV2Slice.reducer;
